import { renderPopin } from './popin-renderer.js'
import { renderLucideIcon } from '../utils/lucide-icons.js'

// Ce renderer constitue l'adaptateur frontend du contrat `email-brief`.
// Le backend reste la source de vérité pour la définition ; le navigateur ne
// fait ici que la normaliser, l'afficher et préparer les patches utilisateur.
function normalizeEmailBriefUi(result) {
  if (!result || typeof result !== 'object') return null

  var ui = result.ui || result.emailBriefUi || null
  if (!ui || typeof ui !== 'object') return null
  if (String(ui.type || '').toLowerCase() !== 'email-brief') return null

  var items = Array.isArray(ui.items)
    ? ui.items.map(normalizeEmailBriefItem).filter(Boolean)
    : []
  var fields = Array.isArray(ui.fields)
    ? ui.fields.map(normalizeEmailBriefField).filter(Boolean)
    : []

  if (!items.length) return null

  return {
    type: 'email-brief',
    items: items,
    fields: fields,
    actions: normalizeActions(ui.actions),
    nextStep: normalizeNextStep(ui.nextStep),
    briefLabels: normalizeBriefLabels(ui.briefLabels),
    modeNotice: normalizeModeNotice(ui.modeNotice),
    recipientCount: normalizeCount(ui.recipientCount),
    definition: cloneObject(ui.definition),
    sendingProfiles: normalizeWritingPolicies(ui.sendingProfiles),
    advancedPrompt: normalizeAdvancedPrompt(ui.advancedPrompt)
  }
}

function normalizeEmailBriefItem(item) {
  if (!item || typeof item !== 'object') return null
  var label = String(item.label || '').trim()
  var value = String(item.value || '').trim()
  if (!label || !value) return null

  return {
    key: String(item.key || '').trim(),
    label: label,
    value: value
  }
}

function normalizeEmailBriefField(field) {
  if (!field || typeof field !== 'object') return null
  var key = String(field.key || '').trim()
  var label = String(field.label || '').trim()
  var control = String(field.control || '').trim()
  if (!key || !label || (control !== 'textarea' && control !== 'select' && control !== 'checkbox')) return null

  return {
    key: key,
    label: label,
    control: control,
    value: String(field.value || '').trim(),
    options: Array.isArray(field.options)
      ? field.options.map(normalizeOption).filter(Boolean)
      : []
  }
}

function normalizeOption(option) {
  if (!option || typeof option !== 'object') return null
  var value = String(option.value || '').trim()
  var label = String(option.label || '').trim()
  if (!value || !label) return null

  return { value: value, label: label }
}

function normalizeActions(actions) {
  return Object.assign({
    edit: 'Modifier',
    save: 'Enregistrer',
    cancel: 'Annuler',
    confirm: 'Confirmer'
  }, actions && typeof actions === 'object' ? actions : {})
}

function normalizeAdvancedPrompt(value) {
  var prompt = value && typeof value === 'object' ? value : {}
  return {
    buttonLabel: String(prompt.buttonLabel || 'Prompt personnalisé'),
    title: String(prompt.title || 'Prompt personnalisé'),
    label: String(prompt.label || 'Consigne'),
    placeholder: String(prompt.placeholder || ''),
    apply: String(prompt.apply || 'Appliquer'),
    clear: String(prompt.clear || 'Effacer')
  }
}

function normalizeNextStep(nextStep) {
  if (!nextStep || typeof nextStep !== 'object') {
    return { key: 'EMailValidateSender', label: 'EMailValidateSender' }
  }

  return {
    key: String(nextStep.key || 'EMailValidateSender'),
    label: String(nextStep.label || nextStep.key || 'EMailValidateSender')
  }
}

function normalizeBriefLabels(labels) {
  return Object.assign({
    constraints: 'Contraintes',
    references: 'Références'
  }, labels && typeof labels === 'object' ? labels : {})
}

function normalizeModeNotice(notice) {
  if (!notice || typeof notice !== 'object') {
    return null
  }

  var message = String(notice.message || '').trim()
  if (!message) return null

  return {
    label: String(notice.label || 'Mode de génération').trim(),
    message: message,
    level: String(notice.level || 'info').toLowerCase() === 'warning' ? 'warning' : 'info'
  }
}

function normalizeWritingPolicies(value) {
  if (!value || typeof value !== 'object') return null
  var options = Array.isArray(value.options)
    ? value.options.map(normalizeWritingPolicyOption).filter(Boolean)
    : []
  if (!options.length) return null

  return {
    selectedPolicyId: String(value.selectedPolicyId || '').trim() || null,
    label: String(value.label || 'Profil d’envoi').trim(),
    options: options
  }
}

function normalizeWritingPolicyOption(option) {
  if (!option || typeof option !== 'object') return null
  var id = String(option.id || '').trim()
  var name = String(option.name || '').trim()
  if (!id || !name) return null

  return {
    id: id,
    name: name,
    description: String(option.description || '').trim(),
    isDefault: option.isDefault === true
  }
}

function normalizeCount(value) {
  var numeric = Number(value)
  return Number.isFinite(numeric) && numeric >= 0 ? Math.floor(numeric) : 0
}

function renderEmailBriefUi(emailBriefUi, options) {
  // L'étape Define est une étape d'édition : elle ne rend ni résumé ni
  // formulaire de consultation. En historique, le brief reste non interactif.
  options = options || {}
  if (options.readOnly === true) return '<div class="wc-email-brief-ui"></div>'
  return '<div class="wc-email-brief-ui">' + renderEmailBriefEditPanel(emailBriefUi) + '</div>'
}

function renderEmailBriefEditPanel(emailBriefUi) {
  return (
    renderWritingPolicySelector(emailBriefUi.sendingProfiles) +
    renderEmailBriefForm(emailBriefUi) +
    renderModeNotice(emailBriefUi.modeNotice) +
    '<div class="wc-email-brief-actions">' +
      '<button type="button" class="wc-email-brief-button wc-email-brief-advanced" data-email-brief-advanced-open>' + renderLucideIcon('sparkles') + '<span>' + escapeHtml(emailBriefUi.advancedPrompt.buttonLabel) + '</span></button>' +
      '<button type="button" class="wc-email-brief-button wc-email-brief-confirm">' + escapeHtml(emailBriefUi.actions.confirm) + '</button>' +
    '</div>' +
    renderAdvancedPromptPopin(emailBriefUi)
  )
}

function renderAdvancedPromptPopin(emailBriefUi) {
  // La consigne avancée est éditée dans une popin pour garder le formulaire
  // principal lisible ; sa valeur est toutefois persistée dans la même
  // définition locale que les champs du brief.
  var prompt = emailBriefUi.advancedPrompt || normalizeAdvancedPrompt()
  var value = emailBriefUi.definition && emailBriefUi.definition.customPrompt
    ? String(emailBriefUi.definition.customPrompt)
    : ''

  return (
    '<div class="wc-email-brief-advanced-popin-host" data-email-brief-advanced-popin-host hidden>' +
      renderPopin({
        className: 'wc-email-brief-advanced-popin',
        dialogClassName: 'wc-email-brief-advanced-dialog',
        ariaLabel: prompt.title,
        closeAttributes: 'data-popin-close data-email-brief-advanced-close',
        header: '<div class="wc-email-brief-advanced-title">' + renderLucideIcon('sparkles') + '<span>' + escapeHtml(prompt.title) + '</span></div>',
        body:
          '<form class="wc-email-brief-advanced-form">' +
            '<label class="wc-email-brief-advanced-field">' +
              '<span>' + escapeHtml(prompt.label) + '</span>' +
              '<textarea rows="10" maxlength="4000" data-email-brief-advanced-input placeholder="' + escapeAttributeValue(prompt.placeholder || '') + '">' + escapeHtml(value) + '</textarea>' +
            '</label>' +
            '<div class="wc-email-brief-advanced-actions">' +
              '<button type="button" class="wc-email-brief-button wc-email-brief-advanced-clear" data-email-brief-advanced-clear>' + escapeHtml(prompt.clear) + '</button>' +
              '<button type="submit" class="wc-email-brief-button wc-email-brief-save">' + escapeHtml(prompt.apply) + '</button>' +
            '</div>' +
          '</form>'
      }) +
    '</div>'
  )
}

function renderWritingPolicySelector(sendingProfiles) {
  if (!sendingProfiles || !sendingProfiles.options.length) return ''
  return (
    '<label class="wc-email-brief-policy">' +
      '<span>' + escapeHtml(sendingProfiles.label) + '</span>' +
      '<select data-email-writing-policy>' +
        sendingProfiles.options.map(function (option) {
          var selected = option.id === sendingProfiles.selectedPolicyId ? ' selected' : ''
          var label = option.name + (option.isDefault ? ' · défaut' : '')
          return '<option value="' + escapeAttributeValue(option.id) + '"' + selected + '>' + escapeHtml(label) + '</option>'
        }).join('') +
      '</select>' +
    '</label>'
  )
}

function renderModeNotice(notice) {
  // Cette alerte doit rester dans le panneau d'édition, immédiatement après
  // le formulaire, afin d'expliquer le coût du mode choisi au bon moment.
  if (!notice) return ''
  var levelClass = notice.level === 'warning' ? ' warning' : ''
  return (
    '<div class="wc-email-brief-mode-notice' + levelClass + '" data-email-brief-mode-notice>' +
      '<strong data-email-brief-mode-label>' + escapeHtml(notice.label) + '</strong>' +
      '<span data-email-brief-mode-message>' + escapeHtml(notice.message) + '</span>' +
    '</div>'
  )
}

function renderEmailBriefForm(emailBriefUi) {
  if (!emailBriefUi.fields.length) return ''

  return (
    '<form class="wc-email-brief-form">' +
      emailBriefUi.fields.map(renderEmailBriefField).join('') +
    '</form>'
  )
}

function renderEmailBriefField(field) {
  var id = 'wc-email-brief-field-' + escapeAttribute(field.key)
  var control = field.control === 'checkbox'
    ? '<input id="' + id + '" type="checkbox" data-email-brief-field="' + escapeAttributeValue(field.key) + '"' + (field.value === 'true' ? ' checked' : '') + '>'
    : field.control === 'textarea'
    ? '<textarea id="' + id + '" rows="3" data-email-brief-field="' + escapeAttributeValue(field.key) + '">' + escapeHtml(field.value) + '</textarea>'
    : renderEmailBriefSelect(id, field)

  return (
    '<label class="wc-email-brief-field" for="' + id + '">' +
      '<span>' + escapeHtml(field.label) + '</span>' +
      control +
    '</label>'
  )
}

function renderEmailBriefSelect(id, field) {
  return (
    '<select id="' + id + '" data-email-brief-field="' + escapeAttributeValue(field.key) + '">' +
      field.options.map(function (option) {
        var selected = option.value === field.value ? ' selected' : ''
        return '<option value="' + escapeAttributeValue(option.value) + '"' + selected + '>' + escapeHtml(option.label) + '</option>'
      }).join('') +
    '</select>'
  )
}

function bindEmailBriefUi(el, emailBriefUi, handlers, options) {
  if (!el || !emailBriefUi) return
  options = options || {}
  var state = {
    definition: cloneObject(emailBriefUi.definition) || {},
    selectedPolicyId: emailBriefUi.sendingProfiles
      ? emailBriefUi.sendingProfiles.selectedPolicyId
      : null
  }
  var form = el.querySelector('.wc-email-brief-form')
  var confirmButton = el.querySelector('.wc-email-brief-confirm')
  var policySelect = el.querySelector('[data-email-writing-policy]')
  var advancedOpenButton = el.querySelector('[data-email-brief-advanced-open]')
  var advancedPopinHost = el.querySelector('[data-email-brief-advanced-popin-host]')
  var advancedInput = el.querySelector('[data-email-brief-advanced-input]')
  var advancedForm = el.querySelector('.wc-email-brief-advanced-form')
  var advancedClearButton = el.querySelector('[data-email-brief-advanced-clear]')

  if (policySelect) {
    policySelect.addEventListener('change', function () {
      state.selectedPolicyId = String(policySelect.value || '').trim() || null
    })
  }

  if (advancedOpenButton && advancedPopinHost) {
    advancedOpenButton.addEventListener('click', function () {
      if (advancedInput) {
        advancedInput.value = String(state.definition.customPrompt || '')
      }
      advancedPopinHost.removeAttribute('hidden')
      if (advancedInput && typeof advancedInput.focus === 'function') advancedInput.focus()
    })
  }

  if (advancedPopinHost) {
    advancedPopinHost.querySelectorAll('[data-email-brief-advanced-close]').forEach(function (closeControl) {
      closeControl.addEventListener('click', function () {
        advancedPopinHost.setAttribute('hidden', 'hidden')
      })
    })
  }

  if (advancedClearButton && advancedInput) {
    advancedClearButton.addEventListener('click', function () {
      advancedInput.value = ''
      state.definition = setAdvancedInstruction(state.definition, '')
      advancedPopinHost && advancedPopinHost.setAttribute('hidden', 'hidden')
    })
  }

  if (advancedForm && advancedInput) {
    advancedForm.addEventListener('submit', function (event) {
      event.preventDefault()
      state.definition = setAdvancedInstruction(state.definition, advancedInput.value)
      advancedPopinHost && advancedPopinHost.setAttribute('hidden', 'hidden')
    })
  }

  if (form) {
    form.querySelectorAll('[data-email-brief-field]').forEach(function (control) {
      control.addEventListener(control.type === 'checkbox' || control.tagName === 'SELECT' ? 'change' : 'input', function () {
        state.definition = applyFormValues(form, emailBriefUi, state.definition)
        updateModeNotice(el, emailBriefUi, state.definition)
      })
    })

    form.addEventListener('submit', function (event) {
      event.preventDefault()
      state.definition = applyFormValues(form, emailBriefUi, state.definition)
      updateModeNotice(el, emailBriefUi, state.definition)
    })
  }

  if (confirmButton) {
    confirmButton.addEventListener('click', function () {
      if (form) {
        state.definition = applyFormValues(form, emailBriefUi, state.definition)
        updateModeNotice(el, emailBriefUi, state.definition)
      }

      if (handlers && typeof handlers.onConfirm === 'function') {
        handlers.onConfirm({
          label: emailBriefUi.actions.confirm,
          nextStepKey: emailBriefUi.nextStep.key,
          definition: state.definition,
          sendingProfile: emailBriefUi.sendingProfiles
            ? {
              policyId: state.selectedPolicyId || null
            }
            : null
        })
      }
    })
  }
}

function fillFormFromDefinition(form, emailBriefUi, definition) {
  emailBriefUi.fields.forEach(function (field) {
    var control = form.querySelector('[data-email-brief-field="' + escapeSelectorValue(field.key) + '"]')
    if (!control) return
    if (field.control === 'checkbox') control.checked = getFieldValue(field.key, definition) === 'true'
    else control.value = getFieldValue(field.key, definition)
  })
}

function applyFormValues(form, emailBriefUi, previousDefinition) {
  // Les champs HTML sont convertis vers la structure métier attendue par le
  // runtime ; le renderer ne transmet jamais directement le formulaire brut.
  var definition = cloneObject(previousDefinition) || {}
  emailBriefUi.fields.forEach(function (field) {
    var control = form.querySelector('[data-email-brief-field="' + escapeSelectorValue(field.key) + '"]')
    if (!control) return
    setFieldValue(definition, field, field.control === 'checkbox' ? (control.checked ? 'true' : 'false') : String(control.value || '').trim())
  })
  return definition
}

function getFieldValue(key, definition) {
  if (key === 'objective') return definition && definition.objective ? String(definition.objective.description || '') : ''
  if (key === 'tone') return definition && definition.tone ? String(definition.tone.style || '') : ''
  if (key === 'callToAction') return definition && definition.callToAction ? String(definition.callToAction.type || '') : ''
  if (key === 'language') return definition ? String(definition.language || '') : ''
  if (key === 'generationMode') return definition ? String(definition.generationMode || '') : ''
  if (key === 'detectLanguageFromContactCountry') return definition && definition.detectLanguageFromContactCountry ? 'true' : 'false'
  return ''
}

function setFieldValue(definition, field, value) {
  if (field.key === 'objective') {
    definition.objective = Object.assign({}, definition.objective || {}, { description: value })
  }
  else if (field.key === 'tone') {
    definition.tone = Object.assign({}, definition.tone || {}, { style: value })
  }
  else if (field.key === 'callToAction') {
    var previousType = definition.callToAction && definition.callToAction.type
    var nextDescription = previousType === value
      ? definition.callToAction && definition.callToAction.description
      : getOptionLabel(field, value)
    definition.callToAction = Object.assign({}, definition.callToAction || {}, {
      type: value,
      description: nextDescription || null
    })
  }
  else if (field.key === 'language') {
    definition.language = value
  }
  else if (field.key === 'generationMode') {
    definition.generationMode = value
  }
  else if (field.key === 'detectLanguageFromContactCountry') {
    definition.detectLanguageFromContactCountry = value === 'true'
  }
}

function setAdvancedInstruction(previousDefinition, value) {
  var definition = cloneObject(previousDefinition) || {}
  var normalized = String(value || '').trim()
  if (normalized) {
    definition.customPrompt = normalized
  } else {
    delete definition.customPrompt
  }
  return definition
}

function getOptionLabel(field, value) {
  if (!field || !Array.isArray(field.options)) return ''
  var option = field.options.find(function (candidate) { return candidate.value === value })
  return option ? option.label : ''
}

function updateModeNotice(el, emailBriefUi, definition) {
  var noticeEl = el.querySelector('[data-email-brief-mode-notice]')
  if (!noticeEl || !emailBriefUi.modeNotice) return
  var notice = buildModeNotice(emailBriefUi, definition)
  noticeEl.classList.toggle('warning', notice.level === 'warning')
  var label = noticeEl.querySelector('[data-email-brief-mode-label]')
  var message = noticeEl.querySelector('[data-email-brief-mode-message]')
  if (label) label.textContent = notice.label
  if (message) message.textContent = notice.message
}

function buildModeNotice(emailBriefUi, definition) {
  var mode = definition && definition.generationMode ? String(definition.generationMode) : ''
  var recipientCount = Number(emailBriefUi.recipientCount) || 0
  var baseNotice = emailBriefUi.modeNotice
  var tokens = estimateModeTokens(mode, recipientCount || 1)
  var tokenVolume = describeTokenVolume(tokens)

  if (mode === 'ai_adaptive') {
    return {
      label: baseNotice.label,
      level: 'warning',
      message: 'Le mode adaptatif génère des emails plus personnalisés avec une consommation ' + tokenVolume + '.'
    }
  }

  if (mode === 'template') {
    return {
      label: baseNotice.label,
      level: 'info',
      message: 'Le mode template utilise un modèle réutilisable avec variables et une consommation ' + tokenVolume + '.'
    }
  }

  return baseNotice
}

function estimateModeTokens(mode, recipientCount) {
  var count = Math.max(1, Number(recipientCount) || 1)
  if (mode === 'template') return 3200 + count * 35
  if (mode === 'ai_adaptive') return 1800 + count * 850
  return 1800 + count * 1200
}

function describeTokenVolume(value) {
  var tokens = Math.max(0, Math.floor(Number(value) || 0))
  if (tokens >= 20000) return 'élevée'
  if (tokens >= 8000) return 'modérée'
  return 'faible'
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function escapeAttribute(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function escapeAttributeValue(value) {
  return escapeHtml(value)
}

function escapeSelectorValue(value) {
  if (typeof CSS !== 'undefined' && CSS.escape) {
    return CSS.escape(String(value || ''))
  }

  return String(value || '').replace(/"/g, '\\"')
}

function cloneObject(value) {
  if (!value || typeof value !== 'object') return null
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (error) {
    return null
  }
}

export { normalizeEmailBriefUi, renderEmailBriefUi, bindEmailBriefUi }
